import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { DragModal } from 'components/DragModal/DragModal';
import { SavedSearchItem } from 'providers/contexts/extensions/SavedSearches';
import { memo, useCallback, useEffect, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { bloxValues } from 'themes/constants';
import { BloxCustomDataProviderType } from 'utility/types/dataProvider';
import { useCosmosQueryClient } from 'providers/clients/CosmosQueryClientProvider';

type QuickFilterEditModalProps = {
  /**
   * The saved search item to edit.
   */
  payload?: SavedSearchItem;
  /**
   * Whether the modal is open.
   */
  open: boolean;
  /**
   * Callback when the modal is cancelled/closed.
   */
  onCancel: () => void;
};

export const QuickFilterEditModal = memo(
  ({ payload, open, onCancel }: QuickFilterEditModalProps) => {
    const theme = useTheme();
    const dataProvider = useDataProvider<BloxCustomDataProviderType>();
    const notify = useNotify();
    const { queryClient } = useCosmosQueryClient();
    const { t } = useTranslation();
    const [isError, setError] = useState({ status: false, message: '' });
    const [nameError, setNameError] = useState<string>('');
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [quickFilterName, setQuickFilterName] = useState<string>(payload?.name || '');
    const [quickFilterShared, setQuickFilterShared] = useState<string>(
      payload?.shared ? 'public' : 'private',
    );

    // Translation strings
    const strError = t('blox.general.error');
    const strPrivacy = t('blox.general.privacy');
    const strPrivate = t('blox.general.private');
    const strShareable = t('blox.general.shareable');
    const strCancel = t('blox.actions.cancel');
    const strQuickFilterName = t('blox.search.saved.quick_filter_name');
    const strForPrivateUse = t('blox.search.saved.for_working_privately');
    const strForSharedUse = t('blox.search.saved.for_share_with_org');
    const strSaveQuickFilter = t('blox.search.saved.save_quick_filter');
    const strEditQuickFilter = t('blox.search.saved.edit_quick_filter');

    // Callbacks and Handlers
    const closeSnackbar = useCallback(() => {
      setError((prevData) => ({
        status: false,
        message: prevData.message,
      }));
    }, []);

    // Handle privacy radio button change
    const handlePrivacyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setQuickFilterShared(event.target.value);
    };

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setQuickFilterName(event.target.value);
      if (nameError) setNameError('');
    };

    const handleClose = useCallback(() => {
      setNameError('');
      setIsSaving(false);
      setError({ status: false, message: '' });
      setQuickFilterName(payload?.name || '');
      setQuickFilterShared(payload?.shared ? 'public' : 'private');
      onCancel();
    }, [onCancel, payload]);

    const handleSearchSave = useCallback(async () => {
      // Check if the name is not empty
      if (quickFilterName?.trim() === '') {
        setNameError('Name cannot be empty');
        return;
      }

      if (!payload?.id) {
        console.error('[handleSearchSave] No saved search id to update.');
        setNameError('Invalid saved search item. Please try again.');
        return;
      }

      // Reset the name error state
      setNameError('');
      setIsSaving(true);

      try {
        const updatedPayload: SavedSearchItem = {
          ...payload,
          name: quickFilterName,
          shared: quickFilterShared === 'private' ? false : true,
        };

        await dataProvider.updateSavedSearch({
          path: { id: payload.id },
          body: updatedPayload,
        });

        notify('Quick filter updated successfully.', { type: 'success' });

        // Invalidate targeted queries (similar to delete behavior)
        await queryClient.invalidateQueries({ queryKey: ['savedsearch'] });
        await queryClient.invalidateQueries({ queryKey: ['getSavedSearches'] });
        queryClient.invalidateQueries({ queryKey: ['getDefaultSavedSearch'] });
        // Close the modal after saving
        handleClose();
      } catch (error) {
        setIsSaving(false);
        console.error('[handleSearchSave] Error:', error);

        let errMsg = strError;
        if (error instanceof Error) {
          errMsg = error.message;
        } else if (typeof error === 'object' && error !== null) {
          const errorObj = error as Record<string, unknown>;
          errMsg = String(errorObj.message ?? errorObj.axiosMessage ?? strError);
        }

        setError({
          status: true,
          message: errMsg,
        });
      }
    }, [
      quickFilterName,
      quickFilterShared,
      payload,
      dataProvider,
      notify,
      handleClose,
      strError,
      queryClient,
    ]);

    // Update local state when payload changes
    useEffect(() => {
      if (payload) {
        setQuickFilterName(payload.name || '');
        setQuickFilterShared(payload.shared ? 'public' : 'private');
        setNameError('');
        setError({ status: false, message: '' });
      }
    }, [payload]);

    return (
      <DragModal
        open={open}
        preventBackdropClickClose={true}
        preventEscapeKeyClose={true}
        onClose={handleClose}
        isDraggable={false}
        isResizable={false}
        isFullscreenable={false}
        initialWidth={600}
        initialHeight={350}
        header={
          <Typography
            data-testid="quick-filter-edit-modal-title"
            sx={{
              fontSize: bloxValues.xlFontSizeRem,
              fontWeight: bloxValues.fontWeightMedium,
              color: theme.palette.text.primary,
            }}
          >
            {strEditQuickFilter}
          </Typography>
        }
        body={
          <Box
            data-testid="quick-filter-edit-modal-body"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              color: theme.palette.text.primary,
            }}
          >
            <Snackbar
              data-testid="quick-filter-edit-modal-error-snackbar"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              open={isError.status}
              autoHideDuration={5000}
              onClose={closeSnackbar}
            >
              <Alert
                variant="filled"
                severity="error"
                sx={{ width: '100%' }}
              >
                <b>{strError}</b> {isError?.message}
              </Alert>
            </Snackbar>

            <FormControl
              fullWidth
              variant="outlined"
            >
              <TextField
                data-testid="quick-filter-edit-modal-name-input"
                required={true}
                label={strQuickFilterName}
                variant="outlined"
                value={quickFilterName}
                error={Boolean(nameError)}
                helperText={nameError}
                sx={{ mb: 2 }}
                onChange={handleNameChange}
                autoComplete="off"
              />

              <FormLabel
                id="quick-filter-edit-modal-privacy-label"
                sx={{ mb: 1 }}
              >
                {strPrivacy}
              </FormLabel>
              <RadioGroup
                aria-labelledby="quick-filter-edit-modal-privacy-label"
                name="quick-filter-privacy"
                value={quickFilterShared}
                onChange={handlePrivacyChange}
                row
                sx={{
                  gap: 1,
                  '& .MuiFormControlLabel-label': {
                    fontWeight: bloxValues.fontWeightMedium,
                  },
                }}
              >
                <Box>
                  <FormControlLabel
                    data-testid="quick-filter-edit-modal-privacy-private"
                    value="private"
                    control={<Radio size="small" />}
                    label={strPrivate}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: bloxValues.smallFontSizeRem,
                      color: theme.palette.text.secondary,
                      ml: 4,
                    }}
                  >
                    {strForPrivateUse}
                  </Typography>
                </Box>
                <Box>
                  <FormControlLabel
                    data-testid="quick-filter-edit-modal-privacy-shareable"
                    value="public"
                    control={<Radio size="small" />}
                    label={strShareable}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: bloxValues.smallFontSizeRem,
                      color: theme.palette.text.secondary,
                      ml: 4,
                    }}
                  >
                    {strForSharedUse}
                  </Typography>
                </Box>
              </RadioGroup>
            </FormControl>
          </Box>
        }
        footer={
          <Box
            data-testid="quick-filter-edit-modal-footer"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              alignItems: 'center',
              width: '100%',
              gap: 2,
            }}
          >
            <Button
              disableElevation
              data-testid="quick-filter-edit-modal-cancel-button"
              onClick={handleClose}
            >
              {strCancel}
            </Button>
            <Button
              disabled={isSaving}
              disableElevation
              data-testid="quick-filter-edit-modal-save-button"
              variant="contained"
              color="primary"
              onClick={handleSearchSave}
            >
              {strSaveQuickFilter}
            </Button>
          </Box>
        }
      />
    );
  },
);

QuickFilterEditModal.displayName = 'QuickFilterEditModal';
